import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { db } from "../firebase";
import { ref, get, update, push, onValue, set } from "firebase/database";
import { Html5Qrcode } from "html5-qrcode";

export default function ManageBooks() {
  const navigate = useNavigate();
  const location = useLocation();
  const [mode, setMode] = useState("issue");
  const [barcode, setBarcode] = useState("");
  const [teacherId, setTeacherId] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [bookInfo, setBookInfo] = useState(null);
  const [teacherInfo, setTeacherInfo] = useState(null);
  const [recentTransactions, setRecentTransactions] = useState([]);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [searchBookTerm, setSearchBookTerm] = useState("");
  const [searchTeacherTerm, setSearchTeacherTerm] = useState("");
  const [searchBookResults, setSearchBookResults] = useState([]);
  const [searchTeacherResults, setSearchTeacherResults] = useState([]);
  const [showBookSearch, setShowBookSearch] = useState(false);
  const [showTeacherSearch, setShowTeacherSearch] = useState(false);
  const [allBooks, setAllBooks] = useState([]);
  const [allTeachers, setAllTeachers] = useState([]);
  const [showScanner, setShowScanner] = useState(false);
  const [scannerStatus, setScannerStatus] = useState("");
  const html5QrCodeRef = useRef(null);
  
  // Library Settings
  const [settings, setSettings] = useState({
    finePerDay: 10,
    maxBooksPerTeacher: 5,
    issueDays: 7,
    lateFeePerDay: 10,
    maxRenewals: 2,
    weekendGrace: true
  });

  const role = localStorage.getItem("role");

  // Bottom navigation items
const bottomNavItems = [
    { name: "Dashboard", path: "/admin-dashboard", icon: "🏠", activeIcon: "🏠" },
    { name: "Books", path: "/books", icon: "📚", activeIcon: "📚" },
    { name: "Teachers", path: "/teachers", icon: "👩‍🏫", activeIcon: "👩‍🏫" },
    { name: "Issue/Return", path: "/manage-books", icon: "🔄", activeIcon: "🔄" },
    { name: "Reports", path: "/reports", icon: "📊", activeIcon: "📊" },
    { name: "Settings", path: "/settings", icon: "⚙️", activeIcon: "⚙️" }
  ];

  // Initialize barcode scanner - CAMERA ONLY, NO FILE UPLOAD
  const startScanner = async () => {
    setScannerStatus("📷 Starting camera...");
    setShowScanner(true);
    
    // Small delay to ensure DOM is ready
    setTimeout(async () => {
      try {
        // Clean up previous scanner if exists
        if (html5QrCodeRef.current) {
          try {
            await html5QrCodeRef.current.stop();
            await html5QrCodeRef.current.clear();
          } catch (e) {}
          html5QrCodeRef.current = null;
        }

        const scanner = new Html5Qrcode("barcode-scanner-container");
        html5QrCodeRef.current = scanner;

        const config = {
          fps: 30,
          qrbox: { width: 300, height: 300 },
          aspectRatio: 1.0,
          disableFlip: false,
        };

        await scanner.start(
          { facingMode: "environment" }, // Use back camera
          config,
          (decodedText) => {
            // Success callback - barcode detected
            console.log("Scanned:", decodedText);
            setScannerStatus(`✅ Scanned: ${decodedText}`);
            setBarcode(decodedText);
            
            // Fetch book info
            setTimeout(() => {
              fetchBookInfoByBarcode(decodedText);
            }, 500);
            
            // Close scanner after successful scan
            setTimeout(() => {
              stopScanner();
              showMessage("success", `Barcode scanned: ${decodedText}`);
            }, 1500);
          },
          (errorMessage) => {
            // Error callback - keep scanning silently
            // Don't show errors to user
          }
        );
        
        setScannerStatus("📷 Camera ready - point at barcode");
      } catch (err) {
        console.error("Scanner error:", err);
        setScannerStatus("❌ Could not start camera. Please check permissions.");
        setTimeout(() => {
          setShowScanner(false);
        }, 2000);
      }
    }, 100);
  };

  // Stop scanner
  const stopScanner = async () => {
    if (html5QrCodeRef.current) {
      try {
        await html5QrCodeRef.current.stop();
        await html5QrCodeRef.current.clear();
      } catch (e) {
        console.error("Error stopping scanner:", e);
      }
      html5QrCodeRef.current = null;
    }
    setShowScanner(false);
    setScannerStatus("");
  };

  // Fetch book info by barcode
  const fetchBookInfoByBarcode = async (barcodeValue) => {
    try {
      const booksSnap = await get(ref(db, "books"));
      let foundBook = null;
      
      booksSnap.forEach((child) => {
        if (child.val().barcode === barcodeValue) {
          foundBook = { id: child.key, ...child.val() };
        }
      });
      
      setBookInfo(foundBook);
      if (!foundBook) {
        showMessage("error", "Book not found! Please check barcode.");
      }
    } catch (error) {
      console.error("Error fetching book:", error);
    }
  };

  // Fetch settings from Firebase
  useEffect(() => {
    const settingsRef = ref(db, "librarySettings");
    const unsubscribe = onValue(settingsRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        setSettings({
          finePerDay: data.finePerDay || data.lateFeePerDay || 10,
          maxBooksPerTeacher: data.maxBooksPerTeacher || 5,
          issueDays: data.issueDays || 7,
          lateFeePerDay: data.lateFeePerDay || 10,
          maxRenewals: data.maxRenewals || 2,
          weekendGrace: data.weekendGrace !== false
        });
      } else {
        const defaultSettings = {
          finePerDay: 10,
          maxBooksPerTeacher: 5,
          issueDays: 7,
          lateFeePerDay: 10,
          maxRenewals: 2,
          weekendGrace: true,
          updatedAt: new Date().toISOString()
        };
        set(ref(db, "librarySettings"), defaultSettings);
        setSettings(defaultSettings);
      }
    });
    
    return () => unsubscribe();
  }, []);

  // Fetch all books for search
  useEffect(() => {
    const booksRef = ref(db, "books");
    const unsubscribe = onValue(booksRef, (snapshot) => {
      const data = snapshot.val() || {};
      const booksList = Object.entries(data).map(([id, book]) => ({
        id,
        ...book
      }));
      setAllBooks(booksList);
    });
    return () => unsubscribe();
  }, []);

  // Fetch all teachers for search
  useEffect(() => {
    const usersRef = ref(db, "users");
    const unsubscribe = onValue(usersRef, (snapshot) => {
      const data = snapshot.val() || {};
      const teachersList = Object.entries(data)
        .filter(([id, user]) => user.role === "teacher")
        .map(([id, teacher]) => ({
          id,
          ...teacher
        }));
      setAllTeachers(teachersList);
    });
    return () => unsubscribe();
  }, []);

  // Search books
  useEffect(() => {
    if (searchBookTerm.length >= 2) {
      const results = allBooks.filter(book =>
        book.name?.toLowerCase().includes(searchBookTerm.toLowerCase()) ||
        book.author?.toLowerCase().includes(searchBookTerm.toLowerCase()) ||
        book.barcode?.includes(searchBookTerm)
      );
      setSearchBookResults(results.slice(0, 10));
    } else {
      setSearchBookResults([]);
    }
  }, [searchBookTerm, allBooks]);

  // Search teachers
  useEffect(() => {
    if (searchTeacherTerm.length >= 2) {
      const results = allTeachers.filter(teacher =>
        teacher.name?.toLowerCase().includes(searchTeacherTerm.toLowerCase()) ||
        teacher.teacherId?.includes(searchTeacherTerm) ||
        teacher.mobile?.includes(searchTeacherTerm)
      );
      setSearchTeacherResults(results.slice(0, 10));
    } else {
      setSearchTeacherResults([]);
    }
  }, [searchTeacherTerm, allTeachers]);

  // Fetch book info when barcode changes
  useEffect(() => {
    if (barcode.length >= 3 && !showScanner) {
      const timer = setTimeout(() => {
        fetchBookInfo();
      }, 500);
      return () => clearTimeout(timer);
    } else if (!showScanner) {
      setBookInfo(null);
    }
  }, [barcode, showScanner]);

  // Fetch teacher info when teacherId changes
  useEffect(() => {
    if (teacherId.length >= 2) {
      fetchTeacherInfo();
    } else {
      setTeacherInfo(null);
    }
  }, [teacherId]);

  // Fetch recent transactions
  useEffect(() => {
    const transactionsRef = ref(db, "transactions");
    const unsubscribe = onValue(transactionsRef, (snapshot) => {
      const data = snapshot.val() || {};
      const transactions = Object.entries(data)
        .map(([id, txn]) => ({ id, ...txn }))
        .sort((a, b) => new Date(b.issueDate) - new Date(a.issueDate))
        .slice(0, 10);
      setRecentTransactions(transactions);
    });
    
    return () => unsubscribe();
  }, []);

  // Online/Offline handlers
  useEffect(() => {
    const handleOnline = () => {
      setIsOffline(false);
      showMessage("success", "Back online! Data synced.");
    };
    
    const handleOffline = () => {
      setIsOffline(true);
      showMessage("error", "You are offline. Some features may not work.");
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Cleanup scanner on unmount
  useEffect(() => {
    return () => {
      if (html5QrCodeRef.current) {
        html5QrCodeRef.current.stop().catch(() => {});
      }
    };
  }, []);

  const fetchBookInfo = async () => {
    try {
      const booksSnap = await get(ref(db, "books"));
      let foundBook = null;
      
      booksSnap.forEach((child) => {
        if (child.val().barcode === barcode) {
          foundBook = { id: child.key, ...child.val() };
        }
      });
      
      setBookInfo(foundBook);
    } catch (error) {
      console.error("Error fetching book:", error);
    }
  };

  const fetchTeacherInfo = async () => {
    try {
      const usersSnap = await get(ref(db, "users"));
      let foundTeacher = null;
      
      usersSnap.forEach((child) => {
        if (child.val().role === "teacher" && 
            (child.val().teacherId === teacherId || 
             child.val().mobile === teacherId ||
             child.val().name?.toLowerCase().includes(teacherId.toLowerCase()))) {
          foundTeacher = { id: child.key, ...child.val() };
        }
      });
      
      setTeacherInfo(foundTeacher);
    } catch (error) {
      console.error("Error fetching teacher:", error);
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: "", text: "" }), 5000);
  };

  const clearFields = () => {
    setBarcode("");
    setTeacherId("");
    setBookInfo(null);
    setTeacherInfo(null);
    setSearchBookTerm("");
    setSearchTeacherTerm("");
    setShowBookSearch(false);
    setShowTeacherSearch(false);
  };

  const selectBook = (book) => {
    setBarcode(book.barcode);
    setBookInfo(book);
    setShowBookSearch(false);
    setSearchBookTerm("");
  };

  const selectTeacher = (teacher) => {
    setTeacherId(teacher.teacherId || teacher.mobile || teacher.name);
    setTeacherInfo(teacher);
    setShowTeacherSearch(false);
    setSearchTeacherTerm("");
  };

  // Calculate days between dates (excluding weekends if enabled)
  const calculateDaysLate = (returnDate, dueDate) => {
    let days = 0;
    let current = new Date(dueDate);
    const end = new Date(returnDate);
    
    while (current < end) {
      current.setDate(current.getDate() + 1);
      if (settings.weekendGrace) {
        const dayOfWeek = current.getDay();
        if (dayOfWeek !== 0 && dayOfWeek !== 6) {
          days++;
        }
      } else {
        days++;
      }
    }
    return days;
  };

  // Issue Book
  const issueBook = async () => {
    if (!barcode.trim()) {
      showMessage("error", "Please enter or scan book barcode");
      return;
    }
    
    if (!teacherId.trim()) {
      showMessage("error", "Please enter Teacher ID or Name");
      return;
    }
    
    if (!bookInfo) {
      showMessage("error", "Book not found. Please check the barcode.");
      return;
    }
    
    if (!teacherInfo) {
      showMessage("error", "Teacher not found. Please check the ID/Name.");
      return;
    }
    
    if (bookInfo.available <= 0) {
      showMessage("error", `"${bookInfo.name}" is out of stock! Available: ${bookInfo.available}`);
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Check if teacher already has this book issued
      const txnSnap = await get(ref(db, "transactions"));
      let alreadyIssued = false;
      
      txnSnap.forEach((child) => {
        if ((child.val().teacherId === teacherInfo.teacherId || child.val().teacherId === teacherInfo.id) && 
            child.val().barcode === barcode && 
            child.val().status === "issued") {
          alreadyIssued = true;
        }
      });
      
      if (alreadyIssued) {
        showMessage("error", "This teacher already has this book issued. Please return it first.");
        setIsLoading(false);
        return;
      }
      
      // Check teacher's total issued books against settings
      let teacherBookCount = 0;
      txnSnap.forEach((child) => {
        if ((child.val().teacherId === teacherInfo.teacherId || child.val().teacherId === teacherInfo.id) && 
            child.val().status === "issued") {
          teacherBookCount++;
        }
      });
      
      if (teacherBookCount >= settings.maxBooksPerTeacher) {
        showMessage("error", `Teacher already has ${settings.maxBooksPerTeacher} books issued. Please return some books first.`);
        setIsLoading(false);
        return;
      }
      
      // Update book availability
      await update(ref(db, `books/${bookInfo.id}`), {
        available: bookInfo.available - 1
      });
      
      // Create transaction
      const txnRef = push(ref(db, "transactions"));
      const issueDate = new Date();
      const dueDate = new Date();
      dueDate.setDate(issueDate.getDate() + settings.issueDays);
      
      await set(txnRef, {
        teacherId: teacherInfo.teacherId || teacherInfo.id,
        teacherName: teacherInfo.name,
        teacherMobile: teacherInfo.mobile || "",
        bookId: bookInfo.id,
        bookTitle: bookInfo.name,
        bookAuthor: bookInfo.author,
        barcode: barcode,
        issueDate: issueDate.toISOString(),
        dueDate: dueDate.toISOString(),
        status: "issued",
        fine: 0,
        issueDays: settings.issueDays,
        createdBy: localStorage.getItem("userId") || "admin",
        createdAt: new Date().toISOString()
      });
      
      showMessage("success", `✅ Book "${bookInfo.name}" issued to ${teacherInfo.name} for ${settings.issueDays} days`);
      clearFields();
      
      // Refresh book info
      await fetchBookInfo();
      
    } catch (error) {
      console.error("Error issuing book:", error);
      showMessage("error", "Failed to issue book. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // Return Book
  const returnBook = async () => {
    if (!barcode.trim()) {
      showMessage("error", "Please enter or scan book barcode");
      return;
    }
    
    setIsLoading(true);
    
    try {
      const txnSnap = await get(ref(db, "transactions"));
      
      let txnKey = null;
      let txnData = null;
      
      txnSnap.forEach((child) => {
        if (child.val().barcode === barcode && child.val().status === "issued") {
          txnKey = child.key;
          txnData = child.val();
        }
      });
      
      if (!txnData) {
        showMessage("error", "No active issue found for this barcode");
        setIsLoading(false);
        return;
      }
      
      const returnDate = new Date();
      const dueDate = new Date(txnData.dueDate);
      
      let fine = 0;
      let daysLate = 0;
      
      if (returnDate > dueDate) {
        daysLate = calculateDaysLate(returnDate, dueDate);
        fine = daysLate * settings.finePerDay;
      }
      
      // Update transaction
      await update(ref(db, `transactions/${txnKey}`), {
        returnDate: returnDate.toISOString(),
        fine: fine,
        daysLate: daysLate,
        status: "returned",
        returnedBy: localStorage.getItem("userId") || "admin",
        returnedAt: new Date().toISOString()
      });
      
      // Update book availability
      const bookRef = ref(db, `books/${txnData.bookId}`);
      const bookSnap = await get(bookRef);
      const currentBook = bookSnap.val();
      
      await update(bookRef, {
        available: (currentBook.available || 0) + 1
      });
      
      const fineMessage = fine > 0 ? ` Fine: ₹${fine} (${daysLate} days late @ ₹${settings.finePerDay}/day)` : " No fine";
      showMessage("success", `✅ Book returned successfully!${fineMessage}`);
      
      clearFields();
      await fetchBookInfo();
      
    } catch (error) {
      console.error("Error returning book:", error);
      showMessage("error", "Failed to return book. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = () => {
    if (mode === "issue") {
      issueBook();
    } else {
      returnBook();
    }
  };

  const calculateDueDate = () => {
    const date = new Date();
    date.setDate(date.getDate() + settings.issueDays);
    return date.toLocaleDateString();
  };

  const handleNavigation = (path) => {
    navigate(path);
  };

  const handleLogout = () => {
    if (window.confirm("Are you sure you want to logout?")) {
      localStorage.clear();
      navigate("/");
    }
  };

  // Access control
  if (role !== "admin") {
    return (
      <div style={styles.accessDenied}>
        <div style={styles.accessDeniedCard}>
          <span style={styles.accessDeniedIcon}>🔒</span>
          <h2>Access Denied</h2>
          <p>You don't have permission to access this page.</p>
          <button onClick={() => navigate("/admin-dashboard")} style={styles.backBtn}>
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const userName = localStorage.getItem("userName") || "Admin";
  const activeIssues = recentTransactions.filter(t => t.status === "issued").length;

  return (
    <div style={styles.container}>
      {/* Offline Banner */}
      {isOffline && (
        <div style={styles.offlineBanner}>
          ⚠️ You are offline. Some features may not work.
        </div>
      )}

      {/* Message Alert */}
      {message.text && (
        <div style={{...styles.alert, ...(message.type === "success" ? styles.alertSuccess : styles.alertError)}}>
          {message.type === "success" ? "✅" : "❌"} {message.text}
        </div>
      )}

      {/* Barcode Scanner Modal - CAMERA ONLY */}
      {showScanner && (
        <div style={styles.scannerModal}>
          <div style={styles.scannerContent}>
            <div style={styles.scannerHeader}>
              <h3>📷 Scan Barcode</h3>
              <button onClick={stopScanner} style={styles.closeScannerBtn}>✕</button>
            </div>
            <div style={styles.scannerContainer}>
              <div id="barcode-scanner-container" style={styles.scannerVideo}></div>
            </div>
            <p style={styles.scannerStatus}>{scannerStatus || "Position barcode in front of camera"}</p>
            <p style={styles.scannerHint}>
              📱 Point camera at any barcode<br/>
              🔄 Scanner will auto-detect and close
            </p>
            <button onClick={stopScanner} style={styles.cancelScannerBtn}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>📚 Book Management</h1>
          <p style={styles.subtitle}>Issue and return books to teachers</p>
        </div>
        <div style={styles.headerRight}>
          <div style={styles.adminProfile} onClick={() => setShowMobileMenu(!showMobileMenu)}>
            <div style={styles.adminAvatar}>
              {userName.charAt(0).toUpperCase()}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Menu Dropdown */}
      {showMobileMenu && (
        <div style={styles.mobileMenu}>
          <div style={styles.mobileMenuItem} onClick={() => { navigate("/profile"); setShowMobileMenu(false); }}>
            <span>👤</span> My Profile
          </div>
          <div style={styles.mobileMenuItem} onClick={() => { navigate("/settings"); setShowMobileMenu(false); }}>
            <span>⚙️</span> Settings
          </div>
          <div style={styles.mobileMenuItem} onClick={() => { handleLogout(); setShowMobileMenu(false); }}>
            <span>🚪</span> Logout
          </div>
        </div>
      )}

      {/* Library Rules Banner */}
      <div style={styles.rulesBanner}>
        <span>📋 Library Rules</span>
        <div style={styles.rulesList}>
          <span>📚 Max: {settings.maxBooksPerTeacher} books/teacher</span>
          <span>📅 Due in {settings.issueDays} days</span>
          <span>💰 Fine: ₹{settings.finePerDay}/day late</span>
          {settings.weekendGrace && <span>🎉 Weekend grace active</span>}
        </div>
      </div>

      {/* Statistics Cards */}
      <div style={styles.statsGrid}>
        <div style={styles.statCard}>
          <span style={styles.statIcon}>📚</span>
          <div>
            <h3 style={styles.statValue}>{allBooks.length}</h3>
            <p style={styles.statLabel}>Total Books</p>
          </div>
        </div>
        <div style={styles.statCard}>
          <span style={styles.statIcon}>👩‍🏫</span>
          <div>
            <h3 style={styles.statValue}>{allTeachers.length}</h3>
            <p style={styles.statLabel}>Total Teachers</p>
          </div>
        </div>
        <div style={styles.statCard}>
          <span style={styles.statIcon}>📖</span>
          <div>
            <h3 style={styles.statValue}>{activeIssues}</h3>
            <p style={styles.statLabel}>Active Issues</p>
          </div>
        </div>
      </div>

      {/* Main Card */}
      <div style={styles.card}>
        {/* Mode Toggle */}
        <div style={styles.toggleContainer}>
          <button
            style={mode === "issue" ? styles.activeToggle : styles.toggle}
            onClick={() => {
              setMode("issue");
              clearFields();
            }}
          >
            <span>📖</span> Issue Book
          </button>
          <button
            style={mode === "return" ? styles.activeToggle : styles.toggle}
            onClick={() => {
              setMode("return");
              clearFields();
            }}
          >
            <span>🔄</span> Return Book
          </button>
        </div>

        {/* Form */}
        <div style={styles.form}>
          {/* Book Barcode Input with Scanner Button */}
          <div style={styles.formGroup}>
            <label style={styles.label}>
              Book Barcode / Search *
              <span style={styles.hint}>Scan barcode or enter manually</span>
            </label>
            
            <div style={styles.barcodeInputGroup}>
              <div style={styles.inputWrapper}>
                <span style={styles.inputIcon}>🔖</span>
                <input
                  type="text"
                  placeholder="Enter book barcode manually"
                  value={barcode}
                  onChange={(e) => setBarcode(e.target.value)}
                  style={styles.input}
                  disabled={isLoading}
                />
              </div>
              <button 
                onClick={startScanner} 
                style={styles.scannerButton}
                type="button"
              >
                📷 Scan Barcode
              </button>
            </div>
            
            {bookInfo && (
              <div style={styles.bookPreview}>
                <div style={styles.bookPreviewIcon}>📚</div>
                <div style={styles.bookPreviewInfo}>
                  <strong>{bookInfo.name}</strong>
                  <span>Author: {bookInfo.author}</span>
                  <span>Available: {bookInfo.available} / {bookInfo.total} copies</span>
                </div>
              </div>
            )}
          </div>

          {/* Book Search (Alternative) */}
          <div style={styles.orDivider}>
            <span>OR Search by Name</span>
          </div>
          
          <div style={styles.searchWrapper}>
            <span style={styles.searchIcon}>🔍</span>
            <input
              type="text"
              placeholder="Search by book name or author..."
              value={searchBookTerm}
              onChange={(e) => {
                setSearchBookTerm(e.target.value);
                setShowBookSearch(true);
              }}
              onFocus={() => setShowBookSearch(true)}
              style={styles.searchInput}
            />
            {searchBookTerm && (
              <button onClick={() => {
                setSearchBookTerm("");
                setShowBookSearch(false);
              }} style={styles.clearSearchBtn}>
                ✕
              </button>
            )}
          </div>
          
          {/* Search Results */}
          {showBookSearch && searchBookResults.length > 0 && (
            <div style={styles.searchResults}>
              {searchBookResults.map(book => (
                <div key={book.id} style={styles.searchResultItem} onClick={() => selectBook(book)}>
                  <span style={styles.resultIcon}>📚</span>
                  <div style={styles.resultInfo}>
                    <strong>{book.name}</strong>
                    <span>{book.author} • Stock: {book.available}/{book.total}</span>
                    <small>Barcode: {book.barcode}</small>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Teacher Search/Input (only for issue mode) */}
          {mode === "issue" && (
            <>
              <div style={styles.orDivider}>
                <span>Teacher Details</span>
              </div>
              
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  Teacher Search *
                  <span style={styles.hint}>Search by name, ID, or mobile</span>
                </label>
                
                <div style={styles.searchWrapper}>
                  <span style={styles.searchIcon}>👩‍🏫</span>
                  <input
                    type="text"
                    placeholder="Search by teacher name, ID, or mobile..."
                    value={searchTeacherTerm}
                    onChange={(e) => {
                      setSearchTeacherTerm(e.target.value);
                      setShowTeacherSearch(true);
                    }}
                    onFocus={() => setShowTeacherSearch(true)}
                    style={styles.searchInput}
                  />
                  {searchTeacherTerm && (
                    <button onClick={() => {
                      setSearchTeacherTerm("");
                      setShowTeacherSearch(false);
                    }} style={styles.clearSearchBtn}>
                      ✕
                    </button>
                  )}
                </div>
                
                {/* Search Results */}
                {showTeacherSearch && searchTeacherResults.length > 0 && (
                  <div style={styles.searchResults}>
                    {searchTeacherResults.map(teacher => (
                      <div key={teacher.id} style={styles.searchResultItem} onClick={() => selectTeacher(teacher)}>
                        <span style={styles.resultIcon}>👩‍🏫</span>
                        <div style={styles.resultInfo}>
                          <strong>{teacher.name}</strong>
                          <span>ID: {teacher.teacherId} • Mobile: {teacher.mobile || "N/A"}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Manual Teacher ID Input */}
                <div style={styles.orDivider}>
                  <span>OR Enter Manually</span>
                </div>
                
                <div style={styles.inputWrapper}>
                  <span style={styles.inputIcon}>🆔</span>
                  <input
                    type="text"
                    placeholder="Enter Teacher ID or Mobile Number"
                    value={teacherId}
                    onChange={(e) => setTeacherId(e.target.value)}
                    style={styles.input}
                    disabled={isLoading}
                  />
                </div>
                
                {teacherInfo && (
                  <div style={styles.teacherPreview}>
                    <div style={styles.teacherPreviewAvatar}>
                      {teacherInfo.name?.charAt(0).toUpperCase() || "T"}
                    </div>
                    <div style={styles.teacherPreviewInfo}>
                      <strong>{teacherInfo.name}</strong>
                      <span>📞 {teacherInfo.mobile || "No mobile"}</span>
                      <span>🆔 ID: {teacherInfo.teacherId}</span>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}

          {/* Info Box for Issue Mode */}
          {mode === "issue" && teacherInfo && bookInfo && bookInfo.available > 0 && (
            <div style={styles.infoBox}>
              <div style={styles.infoBoxIcon}>ℹ️</div>
              <div style={styles.infoBoxContent}>
                <p><strong>Issue Summary:</strong></p>
                <p>📚 Book: {bookInfo.name}</p>
                <p>👨‍🏫 Teacher: {teacherInfo.name}</p>
                <p>📅 Issue Date: {new Date().toLocaleDateString()}</p>
                <p>📅 Due Date: {calculateDueDate()}</p>
                <p style={styles.infoBoxNote}>
                  Note: Books must be returned within {settings.issueDays} days. 
                  Late fee: ₹{settings.finePerDay}/day
                  {settings.weekendGrace && " (Weekends excluded)"}
                </p>
              </div>
            </div>
          )}

          {/* Info Box for Return Mode */}
          {mode === "return" && bookInfo && (
            <div style={styles.infoBox}>
              <div style={styles.infoBoxIcon}>ℹ️</div>
              <div style={styles.infoBoxContent}>
                <p><strong>Returning:</strong> {bookInfo.name}</p>
                <p>Available copies after return: {bookInfo.available + 1}</p>
                <p>Late fee: ₹{settings.finePerDay}/day if overdue</p>
              </div>
            </div>
          )}

          {/* Action Button */}
          <button
            onClick={handleSubmit}
            style={{
              ...styles.actionBtn,
              ...(mode === "issue" ? styles.issueBtn : styles.returnBtn),
              ...(isLoading ? styles.disabledBtn : {})
            }}
            disabled={isLoading}
          >
            {isLoading ? (
              <span style={styles.loaderContainer}>
                <span style={styles.loader}></span>
                Processing...
              </span>
            ) : mode === "issue" ? (
              "📖 Issue Book"
            ) : (
              "🔄 Return Book"
            )}
          </button>
        </div>
      </div>

      {/* Recent Transactions */}
      <div style={styles.recentCard}>
        <h3 style={styles.recentTitle}>📋 Recent Transactions</h3>
        <div style={styles.transactionList}>
          {recentTransactions.length === 0 ? (
            <p style={styles.noData}>No transactions yet</p>
          ) : (
            recentTransactions.map((txn) => (
              <div key={txn.id} style={styles.transactionItem}>
                <div style={styles.transactionIcon}>
                  {txn.status === "issued" ? "📖" : "✅"}
                </div>
                <div style={styles.transactionDetails}>
                  <div style={styles.transactionHeader}>
                    <strong>{txn.bookTitle || "Unknown Book"}</strong>
                    <span style={txn.status === "issued" ? styles.statusIssued : styles.statusReturned}>
                      {txn.status === "issued" ? "Issued" : "Returned"}
                    </span>
                  </div>
                  <div style={styles.transactionMeta}>
                    <span>👤 {txn.teacherName}</span>
                    <span>📅 {new Date(txn.issueDate).toLocaleDateString()}</span>
                    {txn.dueDate && txn.status === "issued" && (
                      <span>📅 Due: {new Date(txn.dueDate).toLocaleDateString()}</span>
                    )}
                    {txn.fine > 0 && <span style={styles.fine}>💰 ₹{txn.fine} fine</span>}
                    {txn.daysLate > 0 && <span>📆 {txn.daysLate} days late</span>}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Quick Tips */}
      <div style={styles.tipsCard}>
        <h4 style={styles.tipsTitle}>💡 Quick Tips</h4>
        <ul style={styles.tipsList}>
          <li>📷 Click "Scan Barcode" button to open camera and scan</li>
          <li>🔍 Use search to quickly find books by name or author</li>
          <li>📚 Maximum {settings.maxBooksPerTeacher} books per teacher at a time</li>
          <li>📅 Books issued for {settings.issueDays} days</li>
          <li>💰 Late return fee: ₹{settings.finePerDay} per day</li>
          <li>👩‍🏫 Search teachers by name, ID, or mobile number</li>
        </ul>
      </div>

      {/* Bottom Navigation Bar */}
      <div style={styles.bottomNav}>
        {bottomNavItems.map((item) => (
          <div
            key={item.path}
            style={{
              ...styles.bottomNavItem,
              ...(location.pathname === item.path && styles.bottomNavItemActive)
            }}
            onClick={() => handleNavigation(item.path)}
          >
            <div style={styles.bottomNavIcon}>
              {location.pathname === item.path ? item.activeIcon : item.icon}
            </div>
            <span style={styles.bottomNavLabel}>{item.name}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

const styles = {
  container: {
    maxWidth: "1200px",
    margin: "0 auto",
    padding: "clamp(16px, 4vw, 32px)",
    paddingBottom: "80px",
    fontFamily: "'Segoe UI', 'Inter', system-ui, sans-serif",
    backgroundColor: "#f7fafc",
    minHeight: "100vh",
    overflowY: "auto",  // Add this
    WebkitOverflowScrolling: "touch",  // Add this
  },

  offlineBanner: {
    background: "#ed8936",
    color: "#fff",
    padding: "12px",
    textAlign: "center",
    borderRadius: "8px",
    marginBottom: "20px",
    fontWeight: "500",
  },

  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    flexWrap: "wrap",
    gap: "16px",
    marginBottom: "24px",
  },

  title: {
    fontSize: "clamp(24px, 5vw, 32px)",
    fontWeight: "700",
    margin: "0 0 8px 0",
    color: "#2d3748",
  },

  subtitle: {
    fontSize: "14px",
    color: "#718096",
    margin: 0,
  },

  headerRight: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
  },

  adminProfile: {
    cursor: "pointer",
  },

  adminAvatar: {
    width: "40px",
    height: "40px",
    borderRadius: "50%",
    background: "linear-gradient(135deg, #667eea, #764ba2)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#fff",
    fontWeight: "bold",
    fontSize: "18px",
  },

  mobileMenu: {
    position: "absolute",
    top: "70px",
    right: "20px",
    backgroundColor: "#fff",
    borderRadius: "12px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
    zIndex: 1000,
    minWidth: "160px",
    overflow: "hidden",
  },

  mobileMenuItem: {
    padding: "12px 16px",
    display: "flex",
    alignItems: "center",
    gap: "12px",
    fontSize: "14px",
    color: "#2d3748",
    cursor: "pointer",
    transition: "background 0.2s ease",
    borderBottom: "1px solid #e2e8f0",
  },

  rulesBanner: {
    backgroundColor: "#667eea",
    color: "#fff",
    padding: "12px 16px",
    borderRadius: "12px",
    marginBottom: "20px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "12px",
  },

  rulesList: {
    display: "flex",
    gap: "16px",
    flexWrap: "wrap",
    fontSize: "12px",
  },

  statsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
    gap: "16px",
    marginBottom: "24px",
  },

  statCard: {
    backgroundColor: "#fff",
    padding: "16px",
    borderRadius: "12px",
    display: "flex",
    alignItems: "center",
    gap: "12px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
    border: "1px solid #e2e8f0",
  },

  statIcon: {
    fontSize: "32px",
  },

  statValue: {
    fontSize: "24px",
    fontWeight: "700",
    margin: 0,
    color: "#2d3748",
  },

  statLabel: {
    fontSize: "12px",
    color: "#718096",
    margin: 0,
  },

  alert: {
    padding: "14px 18px",
    borderRadius: "12px",
    marginBottom: "20px",
    fontSize: "14px",
    fontWeight: "500",
  },

  alertSuccess: {
    backgroundColor: "#c6f6d5",
    color: "#22543d",
    border: "1px solid #9ae6b4",
  },

  alertError: {
    backgroundColor: "#fed7d7",
    color: "#742a2a",
    border: "1px solid #fc8181",
  },

  card: {
    backgroundColor: "#fff",
    borderRadius: "20px",
    padding: "clamp(20px, 5vw, 32px)",
    marginBottom: "24px",
    boxShadow: "0 4px 6px rgba(0,0,0,0.07)",
    border: "1px solid #e2e8f0",
  },

  toggleContainer: {
    display: "flex",
    gap: "12px",
    marginBottom: "32px",
    backgroundColor: "#f1f5f9",
    padding: "6px",
    borderRadius: "14px",
  },

  toggle: {
    flex: 1,
    padding: "12px",
    backgroundColor: "transparent",
    border: "none",
    borderRadius: "10px",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer",
    transition: "all 0.3s ease",
    color: "#64748b",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
  },

  activeToggle: {
    flex: 1,
    padding: "12px",
    backgroundColor: "#fff",
    border: "none",
    borderRadius: "10px",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer",
    transition: "all 0.3s ease",
    color: "#667eea",
    boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
  },

  form: {
    display: "flex",
    flexDirection: "column",
    gap: "20px",
  },

  formGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },

  label: {
    fontSize: "14px",
    fontWeight: "600",
    color: "#2d3748",
  },

  hint: {
    fontSize: "11px",
    fontWeight: "normal",
    color: "#a0aec0",
    marginLeft: "8px",
  },

  barcodeInputGroup: {
    display: "flex",
    gap: "12px",
  },

  scannerButton: {
    padding: "12px 24px",
    backgroundColor: "#667eea",
    color: "#fff",
    border: "none",
    borderRadius: "12px",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer",
    transition: "all 0.3s ease",
    whiteSpace: "nowrap",
  },

  scannerModal: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0,0,0,0.95)",
    zIndex: 2000,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  scannerContent: {
    backgroundColor: "#fff",
    borderRadius: "20px",
    width: "90%",
    maxWidth: "500px",
    overflow: "hidden",
  },

  scannerHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "16px 20px",
    borderBottom: "1px solid #e2e8f0",
  },

  closeScannerBtn: {
    background: "none",
    border: "none",
    fontSize: "24px",
    cursor: "pointer",
    padding: "0 8px",
  },

  scannerContainer: {
    width: "100%",
    padding: "20px",
    backgroundColor: "#000",
    minHeight: "300px",
  },

  scannerVideo: {
    width: "100%",
    height: "auto",
    minHeight: "250px",
  },

  scannerStatus: {
    textAlign: "center",
    padding: "12px",
    fontSize: "13px",
    color: "#4a5568",
  },

  scannerHint: {
    textAlign: "center",
    padding: "12px",
    fontSize: "11px",
    color: "#a0aec0",
    margin: 0,
  },

  cancelScannerBtn: {
    width: "calc(100% - 40px)",
    margin: "0 20px 20px 20px",
    padding: "12px",
    backgroundColor: "#e53e3e",
    color: "#fff",
    border: "none",
    borderRadius: "10px",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer",
  },

  inputWrapper: {
    position: "relative",
    flex: 1,
  },

  inputIcon: {
    position: "absolute",
    left: "14px",
    top: "50%",
    transform: "translateY(-50%)",
    fontSize: "16px",
  },

  input: {
    width: "100%",
    padding: "12px 16px 12px 40px",
    borderRadius: "12px",
    border: "2px solid #e2e8f0",
    fontSize: "14px",
    transition: "all 0.3s ease",
    boxSizing: "border-box",
  },

  searchWrapper: {
    position: "relative",
  },

  searchIcon: {
    position: "absolute",
    left: "14px",
    top: "50%",
    transform: "translateY(-50%)",
    fontSize: "16px",
  },

  searchInput: {
    width: "100%",
    padding: "12px 40px 12px 40px",
    borderRadius: "12px",
    border: "2px solid #e2e8f0",
    fontSize: "14px",
    transition: "all 0.3s ease",
    boxSizing: "border-box",
  },

  clearSearchBtn: {
    position: "absolute",
    right: "12px",
    top: "50%",
    transform: "translateY(-50%)",
    background: "#e2e8f0",
    border: "none",
    borderRadius: "50%",
    width: "22px",
    height: "22px",
    cursor: "pointer",
    fontSize: "12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  searchResults: {
    marginTop: "8px",
    border: "1px solid #e2e8f0",
    borderRadius: "12px",
    maxHeight: "250px",
    overflowY: "auto",
    backgroundColor: "#fff",
  },

  searchResultItem: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "12px",
    borderBottom: "1px solid #e2e8f0",
    cursor: "pointer",
    transition: "background 0.2s ease",
  },

  resultIcon: {
    fontSize: "24px",
  },

  resultInfo: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    flex: 1,
  },

  orDivider: {
    textAlign: "center",
    position: "relative",
    margin: "8px 0",
    color: "#a0aec0",
    fontSize: "12px",
  },

  bookPreview: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "12px",
    backgroundColor: "#ebf8ff",
    borderRadius: "10px",
    marginTop: "8px",
    border: "1px solid #bee3f8",
  },

  bookPreviewIcon: {
    fontSize: "24px",
  },

  bookPreviewInfo: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    fontSize: "13px",
  },

  teacherPreview: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "12px",
    backgroundColor: "#f0fff4",
    borderRadius: "10px",
    marginTop: "8px",
    border: "1px solid #c6f6d5",
  },

  teacherPreviewAvatar: {
    width: "40px",
    height: "40px",
    borderRadius: "50%",
    backgroundColor: "#48bb78",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "18px",
    fontWeight: "bold",
  },

  teacherPreviewInfo: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    fontSize: "13px",
  },

  infoBox: {
    display: "flex",
    gap: "12px",
    padding: "16px",
    backgroundColor: "#fefcbf",
    borderRadius: "12px",
    border: "1px solid #fefcbf",
  },

  infoBoxIcon: {
    fontSize: "20px",
  },

  infoBoxContent: {
    fontSize: "13px",
    color: "#744210",
    lineHeight: "1.5",
  },

  infoBoxNote: {
    fontSize: "11px",
    marginTop: "8px",
    color: "#975a16",
  },

  actionBtn: {
    width: "100%",
    padding: "14px",
    border: "none",
    borderRadius: "12px",
    fontSize: "16px",
    fontWeight: "600",
    cursor: "pointer",
    transition: "all 0.3s ease",
    marginTop: "8px",
  },

  issueBtn: {
    backgroundColor: "#667eea",
    color: "#fff",
  },

  returnBtn: {
    backgroundColor: "#48bb78",
    color: "#fff",
  },

  disabledBtn: {
    opacity: 0.6,
    cursor: "not-allowed",
  },

  loaderContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
  },

  loader: {
    width: "18px",
    height: "18px",
    border: "2px solid #fff",
    borderTop: "2px solid transparent",
    borderRadius: "50%",
    animation: "spin 0.8s linear infinite",
  },

  recentCard: {
    backgroundColor: "#fff",
    borderRadius: "16px",
    padding: "20px",
    marginBottom: "20px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
    border: "1px solid #e2e8f0",
  },

  recentTitle: {
    fontSize: "16px",
    fontWeight: "600",
    margin: "0 0 16px 0",
    color: "#2d3748",
  },

  transactionList: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },

  transactionItem: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "12px",
    backgroundColor: "#f7fafc",
    borderRadius: "10px",
  },

  transactionIcon: {
    fontSize: "24px",
  },

  transactionDetails: {
    flex: 1,
  },

  transactionHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "4px",
    flexWrap: "wrap",
    gap: "8px",
  },

  transactionMeta: {
    display: "flex",
    gap: "12px",
    fontSize: "11px",
    color: "#718096",
    flexWrap: "wrap",
  },

  statusIssued: {
    fontSize: "11px",
    padding: "2px 8px",
    borderRadius: "20px",
    backgroundColor: "#bee3f8",
    color: "#2c5282",
  },

  statusReturned: {
    fontSize: "11px",
    padding: "2px 8px",
    borderRadius: "20px",
    backgroundColor: "#c6f6d5",
    color: "#22543d",
  },

  fine: {
    color: "#e53e3e",
    fontWeight: "500",
  },

  noData: {
    textAlign: "center",
    color: "#a0aec0",
    padding: "20px",
  },

  tipsCard: {
    backgroundColor: "#edf2f7",
    borderRadius: "12px",
    padding: "16px 20px",
    marginBottom: "20px",
  },

  tipsTitle: {
    fontSize: "13px",
    fontWeight: "600",
    margin: "0 0 10px 0",
    color: "#4a5568",
  },

  tipsList: {
    margin: 0,
    paddingLeft: "20px",
    fontSize: "12px",
    color: "#4a5568",
    lineHeight: "1.6",
  },

  bottomNav: {
    position: "fixed",
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: "#fff",
    display: "flex",
    justifyContent: "space-around",
    alignItems: "center",
    padding: "8px 16px",
    boxShadow: "0 -2px 10px rgba(0,0,0,0.1)",
    zIndex: 100,
    borderTop: "1px solid #e2e8f0",
  },

  bottomNavItem: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "4px",
    cursor: "pointer",
    padding: "6px 12px",
    borderRadius: "8px",
    transition: "all 0.2s ease",
    flex: 1,
  },

  bottomNavItemActive: {
    color: "#667eea",
  },

  bottomNavIcon: {
    fontSize: "22px",
  },

  bottomNavLabel: {
    fontSize: "11px",
    fontWeight: "500",
  },

  accessDenied: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "100vh",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
  },

  accessDeniedCard: {
    textAlign: "center",
    padding: "40px",
    background: "#fff",
    borderRadius: "16px",
    maxWidth: "400px",
    margin: "20px",
  },

  accessDeniedIcon: {
    fontSize: "64px",
    display: "block",
    marginBottom: "16px",
  },

  backBtn: {
    marginTop: "20px",
    padding: "12px 24px",
    background: "#667eea",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    fontSize: "14px",
    cursor: "pointer",
  },
};

// Add animations
const styleSheet = document.createElement("style");
styleSheet.textContent = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
  
  input:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
  
  button:hover:not(:disabled) {
    transform: translateY(-2px);
    filter: brightness(1.05);
  }
  
  .search-result-item:hover {
    background-color: #f7fafc;
  }
  
  @media (max-width: 768px) {
    .transaction-meta {
      flex-direction: column;
      gap: 4px;
    }
    
    .stats-grid {
      grid-template-columns: 1fr;
    }
    
    .toggle-container {
      flex-direction: column;
    }
    
    .rules-banner {
      flex-direction: column;
      text-align: center;
    }
    
    .rules-list {
      justify-content: center;
    }
    
    .barcode-input-group {
      flex-direction: column;
    }
    
    .scanner-button {
      width: 100%;
    }
  }
`;
document.head.appendChild(styleSheet);